import React, { FC, memo, useCallback, useEffect, useReducer } from 'react';

import { Box, HStack, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';

import {
  cloudProviderOptionsWithAll,
  PageHeaderWithIcon,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { Select } from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { ExceptionIcon } from 'components/Icons';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { severityOptions } from 'containers/Inventory/AllowList/Components/AllowListWizard/Steps/AllowListCriteria/ResourceTypeCriteria/utils';
import { ExceptionsTable } from 'containers/PolicyHub/Blueprints/Components/Blueprint/Exceptions/ExceptionsTable';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { actions } from 'containers/PolicyHub/Blueprints/slice';
import { policyTypeOptions } from 'containers/PolicyHub/Components/PoliciesTableHeader/utils';

export const PolicyExceptions: FC = memo(() => {
  const dispatch = useDispatch();
  const { exceptions, blueprint } = useSelector(selectBlueprints);
  const { resourceTypeAliasOptionsWithAll } = useResourceType();
  const { setLearnMore } = useKnowledgeBase();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      resourceType: {},
      currentRecord: {},
      searchTerm: '',
      severity: { label: 'Severity', value: undefined },
      cloud: cloudProviderOptionsWithAll[0],
      policyType: policyTypeOptions[0],
      resourceTypeOptions: [],
      severityOptions: [
        { label: 'Severity', value: undefined },
        ...severityOptions,
      ],
    },
  );

  useEffect(() => {
    const options = resourceTypeAliasOptionsWithAll;
    updateState({ resourceTypeOptions: options, resourceType: options[0] });
  }, [resourceTypeAliasOptionsWithAll]);

  const getExceptions = useCallback(() => {
    dispatch(
      actions.getExceptions({
        q: {
          nativeResource: state.resourceType?.value || undefined,
          severity: state.severity?.value || undefined,
          cloud: state.cloud?.value?.toUpperCase() || undefined,
          page: exceptions.page.info.page_number,
          size: exceptions.page.info.page_size,
          blueprintId: blueprint.data?.id,
          searchTerm: !!state.searchTerm ? state.searchTerm : undefined,
        },
      }),
    );
  }, [
    dispatch,
    state.searchTerm,
    state.resourceType,
    state.cloud,
    exceptions.page.info,
    state.severity,
    blueprint.data?.id,
  ]);

  // get exceptions
  useDeepCompareEffect(() => {
    getExceptions();
  }, [getExceptions]);

  const updateValue = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  return (
    <Stack w="full" h="full" spacing={4}>
      <Box>
        <HStack w="full" justify="space-between">
          <PageHeaderWithIcon
            label={'Exceptions'}
            reversed
            icon={<ExceptionIcon />}
          />
          <LearnMoreButton
            onOpen={() => setLearnMore('exceptions')}
            label="Exceptions"
          />
        </HStack>
      </Box>
      <BorderedBox>
        <HStack w="full">
          <Box w="30%">
            <SearchBox
              value={state.searchTerm}
              onChange={s => {
                updateState({ searchTerm: s });
                if (!s) updateValue('searchTerm', '');
              }}
              onSearch={() => updateValue('searchTerm', state.searchTerm)}
              onClickClose={() => {
                updateValue('searchTerm', '');
              }}
              placeholder="Search..."
            />
          </Box>
          <HStack>
            <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
            <Box zIndex={900}>
              <Select
                options={cloudProviderOptionsWithAll}
                value={state.cloud}
                onChange={s => updateValue('cloud', s)}
                showIconInValueContainer
                isAFilter
                menuPortalTarget={document.body}
              />
            </Box>
            <Box zIndex={900}>
              <Select
                options={state.resourceTypeOptions}
                value={state.resourceType}
                onChange={s => updateValue('resourceType', s)}
                showIconInValueContainer
                menuPortalTarget={document.body}
                isAFilter
              />
            </Box>
            <Box zIndex={900}>
              <Select
                options={state.severityOptions}
                value={state.severity}
                onChange={s => updateValue('severity', s)}
                showIconInValueContainer
                menuPortalTarget={document.body}
                isAFilter
              />
            </Box>
          </HStack>
        </HStack>
      </BorderedBox>

      <ExceptionsTable />
    </Stack>
  );
});
